/**
 * Temporal Baseline — tracks 7-day and 30-day rolling averages per source
 * and per event type, computing Z-score deviations.
 *
 * - Stores baselines in memory (IndexedDB persistence comes in Phase 6)
 * - Z-score classification: normal (|z|<1), elevated (1-2), spike (>2), quiet (z<-2)
 * - Updates incrementally on each analysis tick
 */

import type { NormalizedEvent } from '@/sources/base-source';
import type { BaselineDeviation } from './types';
import { getEventTimestamp } from './types';

const ONE_HOUR_MS = 60 * 60 * 1000;
const ONE_DAY_MS = 24 * ONE_HOUR_MS;
const THIRTY_DAYS_MS = 30 * ONE_DAY_MS;

// ---------------------------------------------------------------------------
// Internal state
// ---------------------------------------------------------------------------

/**
 * Daily count record for a source+type combination.
 * Keyed by day (floored to midnight UTC).
 */
interface DailyRecord {
  counts: Map<number, number>; // dayTimestamp → count
}

/** Keyed by "source:type" */
const _records = new Map<string, DailyRecord>();

/** Set of event IDs we've already counted (prevents double-counting on re-scan). */
const _processedIds = new Set<string>();
const MAX_PROCESSED_IDS = 100_000;

/** Floor a timestamp to the start of the day (UTC). */
function floorToDay(ms: number): number {
  return Math.floor(ms / ONE_DAY_MS) * ONE_DAY_MS;
}

/** Get or create a daily record for a source:type key. */
function getRecord(key: string): DailyRecord {
  let record = _records.get(key);
  if (!record) {
    record = { counts: new Map() };
    _records.set(key, record);
  }
  return record;
}

/** Trim records older than 30 days. */
function trimOldRecords(): void {
  const cutoff = floorToDay(Date.now() - THIRTY_DAYS_MS);
  for (const [, record] of _records) {
    for (const day of record.counts.keys()) {
      if (day < cutoff) {
        record.counts.delete(day);
      }
    }
  }
}

/** Trim processed IDs set when it gets too large. */
function trimProcessedIds(): void {
  if (_processedIds.size <= MAX_PROCESSED_IDS) return;
  const arr = Array.from(_processedIds);
  _processedIds.clear();
  // Keep the most recent half
  for (let i = Math.floor(arr.length / 2); i < arr.length; i++) {
    _processedIds.add(arr[i]);
  }
}

/** Compute rolling average for the last N days (excluding today). */
function rollingAverage(record: DailyRecord, days: number): { avg: number; stdDev: number } {
  const today = floorToDay(Date.now());
  const cutoff = today - days * ONE_DAY_MS;

  const values: number[] = [];

  // Iterate through each day in the window
  for (let day = cutoff; day < today; day += ONE_DAY_MS) {
    values.push(record.counts.get(day) ?? 0);
  }

  if (values.length === 0) return { avg: 0, stdDev: 0 };

  const sum = values.reduce((a, b) => a + b, 0);
  const avg = sum / values.length;

  // Standard deviation
  const sqDiffSum = values.reduce((acc, val) => acc + (val - avg) ** 2, 0);
  const stdDev = Math.sqrt(sqDiffSum / values.length);

  return { avg, stdDev };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Update baselines with new events.
 * Should be called on each analysis tick.
 * Deduplicates by event ID to prevent double-counting.
 */
export function updateBaselines(events: NormalizedEvent[]): void {
  for (const event of events) {
    if (_processedIds.has(event.id)) continue;
    _processedIds.add(event.id);

    const ts = getEventTimestamp(event);
    const day = floorToDay(ts);
    const key = `${event.source}:${event.type}`;

    const record = getRecord(key);
    record.counts.set(day, (record.counts.get(day) ?? 0) + 1);
  }

  // Periodic cleanup
  trimOldRecords();
  trimProcessedIds();
}

/**
 * Get the baseline deviation for a specific source and event type.
 * Returns Z-score and classification for the current day's count.
 */
export function getDeviation(source: string, type: string): BaselineDeviation {
  const key = `${source}:${type}`;
  const record = _records.get(key);

  const today = floorToDay(Date.now());
  const currentValue = record?.counts.get(today) ?? 0;

  const stats7d = record ? rollingAverage(record, 7) : { avg: 0, stdDev: 0 };
  const stats30d = record ? rollingAverage(record, 30) : { avg: 0, stdDev: 0 };

  // Z-score against 7-day baseline (more sensitive to short-term changes)
  let zScore = 0;
  if (stats7d.stdDev > 0) {
    zScore = (currentValue - stats7d.avg) / stats7d.stdDev;
  } else if (stats7d.avg > 0) {
    // No variance — any deviation is significant
    zScore = currentValue > stats7d.avg ? 2.5 : currentValue < stats7d.avg ? -2.5 : 0;
  }

  // Classify
  let classification: BaselineDeviation['classification'];
  if (zScore > 2) classification = 'spike';
  else if (zScore >= 1) classification = 'elevated';
  else if (zScore < -2) classification = 'quiet';
  else classification = 'normal';

  return {
    source,
    type,
    currentValue,
    avg7d: Math.round(stats7d.avg * 10) / 10,
    avg30d: Math.round(stats30d.avg * 10) / 10,
    zScore: Math.round(zScore * 100) / 100,
    classification,
  };
}

/**
 * Get deviations for all tracked source:type combinations.
 * Useful for the analysis dashboard.
 */
export function getAllDeviations(): BaselineDeviation[] {
  const results: BaselineDeviation[] = [];

  for (const key of _records.keys()) {
    const [source, type] = key.split(':');
    if (source && type) {
      results.push(getDeviation(source, type));
    }
  }

  // Sort by absolute Z-score descending (most anomalous first)
  results.sort((a, b) => Math.abs(b.zScore) - Math.abs(a.zScore));

  return results;
}
